import { useState, useCallback, useRef } from 'react';
import { OCRResult, ProcessingStatus } from '../types/nric';
import { ocrProcessor } from '../utils/ocrUtils';
import { validateImageFile } from '../utils/imageUtils';
import { PROCESSING_STAGES, PROGRESS_STAGES, ERROR_MESSAGES, SUCCESS_MESSAGES } from '../constants';

export const useOCR = () => {
  const [result, setResult] = useState<OCRResult>({
    data: null,
    confidence: 0,
    rawText: '',
    processing: false,
    error: null
  });

  const [status, setStatus] = useState<ProcessingStatus>({
    stage: PROCESSING_STAGES.IDLE,
    progress: 0,
    message: ''
  });

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const processingRef = useRef<boolean>(false);

  const processImage = useCallback(async (file: File) => {
    // Prevent multiple simultaneous processing
    if (processingRef.current) {
      return;
    }

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setStatus({
        stage: PROCESSING_STAGES.ERROR,
        progress: 0,
        message: validation.error || ERROR_MESSAGES.INVALID_FILE_TYPE
      });
      return;
    }

    processingRef.current = true;

    // Create image preview
    const imageUrl = URL.createObjectURL(file);
    setSelectedImage(imageUrl);

    // Reset previous results
    setResult({
      data: null,
      confidence: 0,
      rawText: '',
      processing: true,
      error: null
    });

    try {
      // Stage 1: Loading
      setStatus({
        stage: PROCESSING_STAGES.LOADING,
        progress: PROGRESS_STAGES.LOADING,
        message: 'Preparing image for processing...'
      });

      await new Promise(resolve => setTimeout(resolve, 500));

      // Stage 2: OCR Recognition
      setStatus({
        stage: PROCESSING_STAGES.RECOGNIZING,
        progress: PROGRESS_STAGES.RECOGNIZING_START,
        message: 'Extracting text from image...'
      });

      const ocrResult = await ocrProcessor.processImage(file, (progress) => {
        setStatus(prev => ({
          ...prev,
          progress: PROGRESS_STAGES.RECOGNIZING_START + 
            (progress * (PROGRESS_STAGES.RECOGNIZING_END - PROGRESS_STAGES.RECOGNIZING_START) / 100),
        }));
      });

      // Stage 3: Data Parsing
      setStatus({
        stage: PROCESSING_STAGES.PARSING,
        progress: PROGRESS_STAGES.PARSING,
        message: 'Identifying NRIC information...'
      });

      await new Promise(resolve => setTimeout(resolve, 1000));

      const parsedData = ocrProcessor.parseNRICData(ocrResult.text);

      // Stage 4: Complete
      setStatus({
        stage: PROCESSING_STAGES.COMPLETE,
        progress: PROGRESS_STAGES.COMPLETE,
        message: SUCCESS_MESSAGES.PROCESSING_COMPLETE
      });

      setResult({
        data: parsedData,
        confidence: ocrResult.confidence,
        rawText: ocrResult.text,
        processing: false,
        error: parsedData ? null : ERROR_MESSAGES.NO_NRIC_DATA
      });

      // Auto-hide progress after completion
      setTimeout(() => {
        setStatus({ 
          stage: PROCESSING_STAGES.IDLE, 
          progress: 0, 
          message: '' 
        });
      }, 2000);

    } catch (error) {
      console.error('OCR processing error:', error);
      
      setStatus({
        stage: PROCESSING_STAGES.ERROR,
        progress: 0,
        message: error instanceof Error ? error.message : ERROR_MESSAGES.GENERIC_ERROR
      });

      setResult({
        data: null,
        confidence: 0,
        rawText: '',
        processing: false,
        error: ERROR_MESSAGES.IMAGE_PROCESSING_FAILED
      });
    } finally {
      processingRef.current = false;
    }
  }, []);

  const reset = useCallback(() => {
    if (processingRef.current) {
      return; // Don't reset while processing
    }

    setResult({
      data: null,
      confidence: 0,
      rawText: '',
      processing: false,
      error: null
    });
    
    setStatus({
      stage: PROCESSING_STAGES.IDLE,
      progress: 0,
      message: ''
    });
    
    if (selectedImage) {
      URL.revokeObjectURL(selectedImage);
      setSelectedImage(null);
    }
  }, [selectedImage]);

  return {
    result,
    status,
    selectedImage,
    processImage,
    reset,
    isProcessing: processingRef.current || result.processing
  };
};

